//      
import React, { Component, useState } from 'react';
import { Link } from 'react-router-dom';
import { Formik, Form } from 'formik';
import { sortBy } from 'lodash';

import styled from 'styled-components';

import SiteNotice from 'components/SiteNotice';
import { chainValidators, isRequired, isEmail } from 'utils/formHelpers';
import { Alert, Button, Card, Col, Container, ListGroup, Row } from 'bs';
import { Input } from 'components/FormikFields';
import FormattedDate from 'components/FormattedDate';
import CancelButton from 'components/CancelButton';
import SubmitButton from 'components/SubmitButton';

import { CenterBox } from './utils';

import {                } from 'types/api';

              
                                                                           
                                                                            
                        
                            
                                      
  

const ERROR_429 = 'Too many login attempts. Please wait and try again later.';

const FullCard = styled(Card)`
    height: 100%;
`;

const LoginForm = ({ login }) => {
    return (
        <FullCard title='Sign In'>
            <Formik
                initialValues={{ email: '', password: '' }}
                onSubmit={(values, actions) => {
                    const callbacks = {
                        start: () => {
                            actions.setSubmitting(true);
                            actions.setStatus(null);
                        },
                        done: () => {
                            // unmounted component at this point.
                            return null;
                        },
                        fail: error => {
                            let message = 'Please enter a valid username or password.';
                            actions.setSubmitting(false);
                            if (error.status === 429) {
                                message = ERROR_429;
                            }
                            actions.setStatus(message);
                        },
                    };
                    login(values.email, values.password, callbacks);
                }}
            >
                {({ isValid, isSubmitting, status }) => (
                    <Form className='form' id='form-login'>
                        {status && <Alert flavor='danger'>{status}</Alert>}

                        <Input
                            type='email'
                            name='email'
                            placeholder='Enter email'
                            autoComplete='username'
                            tabIndex='1'
                            label='Email address'
                            validate={chainValidators(isRequired, isEmail)}
                        />
                        <span
                            className='float-right'
                            style={{ zIndex: '2', position: 'relative' }}
                        >
                            <Link to='/reset-password' id='request-password-reset-link'>
                                Reset Password
                            </Link>
                        </span>
                        <Input
                            name='password'
                            type='password'
                            label='Password'
                            autoComplete='current-password'
                            tabIndex='2'
                            validate={isRequired}
                        />

                        <SubmitButton
                            disabled={!isValid}
                            icon='sign-in-alt'
                            iconPositionToLabel='right'
                            submitting={isSubmitting}
                            tabIndex='3'
                        />
                    </Form>
                )}
            </Formik>
        </FullCard>
    );
};

const Register = () => (
    <FullCard title='Create New Account'>
        <p>Please register for your account to:</p>
        <ul>
            <li>View your current home, motor, and health insurance policies</li>
            <li>Renew and make debit/credit card payments</li>
        </ul>

        <Link id='register-link' className='btn btn-primary mt-3' to='/register'>
            Register
        </Link>
    </FullCard>
);

const Device = styled.div`
    &:hover {
        cursor: pointer;
    }
`;

const MFADevices = ({ devices, onSelect, selected }) => (
    <ListGroup
        items={sortBy(devices, ['created_at']).map(device => (
            <Device onClick={() => onSelect(device.id)}>
                <h4 className='mb-0'>{device.name}</h4>
                <small>
                    Added on <FormattedDate date={device.created_at} />
                </small>
            </Device>
        ))}
    />
);

const MFAForm = ({ instructions, loginWithMFA }) => {
    const { device, email, token, uid, url } = instructions;

    let helpText = 'To login, enter the code';
    if (device) {
        helpText = `To login, enter the code from “${device.name}”:`;
    } else if (email) {
        helpText = `To login, enter the code sent to “${email}”:`;
    }

    return (
        <CenterBox>
            <Col className='form-login form-wrapper'>
                <h2>Two-Factor Verification</h2>

                <p className='lead'>
                    Your account has two-factor authentication enabled.
                    <br />
                    {helpText}
                </p>

                <Formik
                    initialValues={{ code: '' }}
                    onSubmit={({ code }, actions) =>
                        loginWithMFA(
                            url,
                            { code, uid, token },
                            {
                                start: () => {
                                    actions.setSubmitting(true);
                                    actions.setStatus(null);
                                },
                                done: () => {
                                    // unmounted component at this point.
                                    return null;
                                },
                                fail: error => {
                                    let message = 'Invalid code.';
                                    actions.setSubmitting(false);
                                    if (error.status === 429) {
                                        message = ERROR_429;
                                    }
                                    actions.setStatus(message);
                                },
                            }
                        )
                    }
                >
                    {({ status, isSubmitting }) => (
                        <Form>
                            {status && (
                                <>
                                    <Alert flavor='danger'>{status}</Alert>
                                    <Alert flavor='info'>Having an issue with your authenticator?<br/><Link to='/support'>Submit a support request.</Link></Alert>
                                </>
                            )}

                            <Input
                                name='code'
                                label='Code'
                                validate={isRequired}
                                inputMode='numeric'
                                pattern='[0-9]*'
                                autoComplete='one-time-code'
                            />

                            <SubmitButton submitting={isSubmitting} />
                        </Form>
                    )}
                </Formik>
            </Col>
        </CenterBox>
    );
};

const SiteSelectionForm = ({ instructions, login }) => {
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const selectRole = role => {
        login(
            instructions.url,
            { role },
            {
                start: () => {
                    setSubmitting(true);
                    setError(null);
                },
                done: () => {
                    // unmounted component at this point.
                    return null;
                },
                fail: error => {
                    setSubmitting(false);
                    setError("Error selecting site.");
                },
            },
        );
    };

    return (
        <CenterBox>
            <Col className='form-login form-wrapper'>
                <h2>Select Role</h2>

                {error && <Alert>{error}</Alert>}

                <p className='lead'>This account handles multiples roles.</p>
                <p className='lead'>Which would you like to do now?</p>

                {instructions.roles.map(([value, label]) => (
                    <Button className='mr-3' disabled={submitting} onClick={() => selectRole(value)}>{label}</Button>
                ))}

            </Col>
        </CenterBox>
    );

};

class Login extends Component        {

    render() {
        const {
            login, loginAlert,
            loginWithMFA, pendingMFA, mfaInstructions,
            loginWithSiteSelection, pendingSiteSelection,
            registrationOpen, siteSelectionInstructions,
        } = this.props;

        let content = null;
        if (pendingMFA) {
            content = (
                <Col xs={12}>
                    <MFAForm
                        instructions={mfaInstructions}
                        loginWithMFA={loginWithMFA}
                    />
                </Col>
            );
        } else if (pendingSiteSelection) {
            content = (
                <Col xs={12}>
                    <SiteSelectionForm
                        instructions={siteSelectionInstructions}
                        login={loginWithSiteSelection}
                    />
                </Col>
            );
        } else {
            content = (
                <React.Fragment>
                    <Col xs={12}>
                        <Alert>{loginAlert}</Alert>
                    </Col>

                    <Col sm={registrationOpen ? 6 : 12} className='mb-3'>
                        <LoginForm login={login} />
                    </Col>

                    {registrationOpen && (
                        <Col sm={6} className='mb-3'>
                            <Register />
                        </Col>
                    )}
                </React.Fragment>
            );
        }

        return (
            <Container breakpoint='sm' className='mt-4'>
                <Row>
                    <Col xs={12}>
                        <SiteNotice />
                    </Col>
                    {content}
                </Row>
            </Container>
        );
    }
}

export default Login;
